import { ScheduledAction, TaskItemForSchedule } from '@demind-inc/core';

import { transformAllPlanV7ScheduleEventsToScheduleAction } from '../../helpers';
import { ScheduleEvent } from '../api/science/openapi';

export const parseV7Response = (
  rawData: unknown
): { schedules: unknown[]; summary: string | null } => {
  const empty = { schedules: [], summary: null };

  if (!rawData) return empty;

  const pickResult = (value: unknown) => {
    if (!value || typeof value !== 'object') return empty;
    const v = value as Record<string, unknown>;
    const resultJson = v.result_json ?? v.chat_result ?? v;
    const rj = resultJson as Record<string, unknown>;
    const schedules = rj?.schedules ?? rj?.schedule ?? [];
    const summary = rj?.summary ?? v?.summary ?? null;
    return {
      schedules: Array.isArray(schedules) ? schedules : [],
      summary: typeof summary === 'string' ? summary : null,
    };
  };

  if (typeof rawData !== 'string') return pickResult(rawData);

  try {
    return pickResult(JSON.parse(rawData));
  } catch {
    return empty;
  }
};

export interface DailyPlanStreamResult {
  schedules: ScheduledAction[];
  summary: string | null;
}

export interface DailyPlanStreamHandlers {
  onStatusText?: (text: string | null) => void;
  onResult?: (result: DailyPlanStreamResult) => void;
}

/**
 * Shared per-event-type parser for the daily-plan stream, used both by the
 * live POST+fetch+eventsource-parser path (useSuggestAllAIPlan.ts) and by the
 * job-recovery poll replay (useDailyPlanJobRecovery.ts) — so a resumed job
 * renders through the exact same logic a live stream would have used.
 */
export const applyDailyPlanStreamEvent = (
  eventType: string,
  rawData: string,
  targetDate: string,
  cachedTasks: TaskItemForSchedule[],
  handlers: DailyPlanStreamHandlers
): void => {
  try {
    if (eventType === 'status_text') {
      let statusText: string | null = null;
      if (rawData) {
        try {
          const obj = JSON.parse(rawData);
          statusText =
            obj.status_text ?? obj.statusText ?? obj.status ?? (typeof obj === 'string' ? obj : null);
        } catch {
          statusText = rawData;
        }
      }
      if (statusText) handlers.onStatusText?.(statusText);
      return;
    }

    if (eventType === 'plan_action') {
      try {
        const data = JSON.parse(rawData);
        const action = data?.action ?? 'update';
        const item = data?.item ?? '';
        const time = data?.time ? ` (${data.time})` : '';
        const reason = data?.reason ? ` — ${data.reason}` : '';
        const text = `${action}${item ? `: ${item}` : ''}${time}${reason}`.trim();
        if (text) handlers.onStatusText?.(text);
      } catch {
        // ignore malformed plan_action
      }
      return;
    }

    if (eventType === 'result_json' || eventType === 'chat_result') {
      const parsed = parseV7Response(rawData);
      const result: DailyPlanStreamResult = {
        schedules: transformAllPlanV7ScheduleEventsToScheduleAction(
          parsed.schedules as ScheduleEvent[],
          targetDate,
          cachedTasks
        ),
        summary: parsed.summary,
      };
      handlers.onResult?.(result);
    }
  } catch {
    // ignore parse errors for this event, continue with the next one
  }
};
